import { Injectable, OnDestroy } from '@angular/core';
import { interval, Observable, Subject, timer } from 'rxjs';
import { switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators';

export interface PollConfig {
  reportId: number;
  interval?: number; // milliseconds between polls, default 3000
  maxDuration?: number; // maximum duration to poll in milliseconds, default 300000 (5 minutes)
}

export interface ReportStatus {
  id: number;
  status: {
    key: number;
    value: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ReportPollingService implements OnDestroy {
  private pollingSubjects: Map<number, Subject<void>> = new Map();
  private readonly destroy$ = new Subject<void>();

  constructor() {}

  /**
   * Start polling for a report status
   * @param config - Polling configuration
   * @param statusCheckFn - Function that checks the report status
   * @param onStatusChange - Callback when status changes
   * @param onComplete - Callback when polling completes (success or failure)
   * @param onError - Callback when an error occurs
   */
  startPolling(
    config: PollConfig,
    statusCheckFn: (reportId: number) => Observable<any>,
    onStatusChange: (status: ReportStatus) => void,
    onComplete?: () => void,
    onError?: (error: any) => void
  ): void {
    const { reportId, interval: pollInterval = 3000, maxDuration = 300000 } = config;

    // Stop existing polling for this report if any
    this.stopPolling(reportId);

    // Create a new subject for this report
    const stopSubject = new Subject<void>();
    this.pollingSubjects.set(reportId, stopSubject);

    const startTime = Date.now();

    // Start polling
    timer(0, pollInterval)
      .pipe(
        switchMap(() => statusCheckFn(reportId)),
        tap((response: any) => {
          if (response?.data) {
            const status: ReportStatus = {
              id: response.data.id,
              status: response.data.status
            };
            
            // Check if report is in final state BEFORE calling onStatusChange
            // Status keys: -1=Queued, 1=Pending, 2=AvailableForDownload, 3=Failed, 4=Downloaded
            const isFinal = this.isFinalState(status.status.key);
            
            // Update status in UI
            onStatusChange(status);

            // Stop polling if in final state
            if (isFinal) {
              this.stopPolling(reportId);
              if (onComplete) {
                onComplete();
              }
            }
          }
        }),
        takeWhile(() => {
          // Stop if max duration exceeded
          const elapsed = Date.now() - startTime;
          if (elapsed > maxDuration) {
            this.stopPolling(reportId);
            if (onError) {
              onError({ message: 'Polling timeout exceeded' });
            }
            return false;
          }
          return true;
        }),
        takeUntil(stopSubject),
        takeUntil(this.destroy$)
      )
      .subscribe({
        error: (error) => {
          this.stopPolling(reportId);
          if (onError) {
            onError(error);
          }
        }
      });
  }

  /**
   * Stop polling for a specific report
   */
  stopPolling(reportId: number): void {
    const subject = this.pollingSubjects.get(reportId);
    if (subject) {
      subject.next();
      subject.complete();
      this.pollingSubjects.delete(reportId);
    }
  }

  /**
   * Stop all active polling
   */
  stopAllPolling(): void {
    this.pollingSubjects.forEach((subject) => {
      subject.next();
      subject.complete();
    });
    this.pollingSubjects.clear();
  }

  /**
   * Check if a status represents a final state
   */
  private isFinalState(statusKey: number): boolean {
    // Status keys: -1=Queued, 1=Pending, 2=AvailableForDownload, 3=Failed, 4=Downloaded
    // Final states: 2=AvailableForDownload, 3=Failed, 4=Downloaded
    // We stop polling when report reaches any final state
    return [2, 3, 4].includes(statusKey);
  }

  /**
   * Check if polling is active for a report
   */
  isPolling(reportId: number): boolean {
    return this.pollingSubjects.has(reportId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopAllPolling();
  }
}

